import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchAdminIdentity, useAuthStore } from "@veorest/api-client";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sidebar,
  Topbar,
  PageSection,
  type SidebarSection,
} from "@veorest/ui";
import {
  LayoutDashboard,
  Store,
  Users,
  UserCircle,
  CreditCard,
  TrendingUp,
  Bike,
  Settings,
  Smartphone,
  Menu,
  ChevronDown,
  LogOut,
  X,
  Shield,
  Handshake,
  ShoppingBag,
} from "lucide-react";
import { ROUTES, APP_ROUTE_PATHS } from "../routes";
import { OeloxLogo } from "../components/OeloxLogo";
import OverviewPage from "./OverviewPage";
import ClientsPage from "./ClientsPage";
import ClientDetailPage from "./ClientDetailPage";
import CustomersGlobalPage from "./CustomersGlobalPage";
import CustomersRestaurantPage from "./CustomersRestaurantPage";
import OrdersOverviewPage from "./OrdersOverviewPage";
import PaymentsPage from "./PaymentsPage";
import ReportsPage from "./ReportsPage";
import DriversPage from "./DriversPage";
import PartnersPage from "./PartnersPage";
import PartnerDetailPage from "./PartnerDetailPage";
import SettingsPage from "./SettingsPage";
import AppSettingsPage from "./AppSettingsPage";
import OrderDetailPage from "./OrderDetailPage";

type NavKey =
  | "overview"
  | "clients"
  | "customers_global"
  | "customers_restaurant"
  | "orders"
  | "payments"
  | "reports"
  | "drivers"
  | "partners"
  | "settings"
  | "app_settings"
  | "client_detail"
  | "partner_detail"
  | "order_detail";

function resolveNavKey(pathname: string): NavKey {
  if (pathname.startsWith("/app/clients/") && pathname !== ROUTES.CLIENTS) return "client_detail";
  if (pathname.startsWith("/app/partners/") && pathname !== ROUTES.PARTNERS) return "partner_detail";
  if (pathname.startsWith("/app/orders/") && pathname !== ROUTES.ORDERS) return "order_detail";
  if (pathname === ROUTES.CLIENTS) return "clients";
  if (pathname === ROUTES.CUSTOMERS_GLOBAL) return "customers_global";
  if (pathname === ROUTES.CUSTOMERS_RESTAURANT) return "customers_restaurant";
  if (pathname === ROUTES.ORDERS) return "orders";
  if (pathname === ROUTES.PAYMENTS) return "payments";
  if (pathname === ROUTES.REPORTS) return "reports";
  if (pathname === ROUTES.DRIVERS) return "drivers";
  if (pathname === ROUTES.PARTNERS) return "partners";
  if (pathname === ROUTES.APP_SETTINGS) return "app_settings";
  if (pathname === ROUTES.SETTINGS) return "settings";
  return "overview";
}

function clientIdFromPath(pathname: string): string {
  const match = pathname.match(/^\/app\/clients\/([^/]+)\/?$/);
  return match?.[1] ?? "";
}

function partnerIdFromPath(pathname: string): string {
  const match = pathname.match(/^\/app\/partners\/([^/]+)\/?$/);
  return match?.[1] ?? "";
}

function orderIdFromPath(pathname: string): string {
  const match = pathname.match(/^\/app\/orders\/([^/]+)\/?$/);
  return match?.[1] ?? "";
}

export default function DashboardPage() {
  const { user, signOut } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const identity = useQuery({
    queryKey: ["admin-identity"],
    queryFn: fetchAdminIdentity,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    document.title = `${identity.data?.brand_name_en ?? "OELOX"} Super Admin`;
  }, [identity.data?.brand_name_en]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const isClientDetail = path.startsWith("/app/clients/") && path !== ROUTES.CLIENTS;
    const isPartnerDetail = path.startsWith("/app/partners/") && path !== ROUTES.PARTNERS;
    const isOrderDetail = path.startsWith("/app/orders/") && path !== ROUTES.ORDERS;
    if (!APP_ROUTE_PATHS.has(path) && !isClientDetail && !isPartnerDetail && !isOrderDetail) {
      navigate(ROUTES.OVERVIEW, { replace: true });
    }
  }, [location.pathname, navigate]);

  const currentKey = resolveNavKey(location.pathname);
  const activeKey =
    currentKey === "client_detail"
      ? "clients"
      : currentKey === "partner_detail"
        ? "partners"
        : currentKey === "order_detail"
          ? "orders"
          : currentKey;
  const clientId = clientIdFromPath(location.pathname);
  const partnerId = partnerIdFromPath(location.pathname);
  const orderId = orderIdFromPath(location.pathname);

  const navIconClass = "w-5 h-5";
  const sidebarSections: SidebarSection[] = [
    {
      items: [
        { key: "overview", label: "Overview", icon: <LayoutDashboard className={navIconClass} />, onClick: () => navigate(ROUTES.OVERVIEW) },
        { key: "clients", label: "Clients", icon: <Store className={navIconClass} />, onClick: () => navigate(ROUTES.CLIENTS) },
        { key: "customers_global", label: "Global Customers", icon: <Users className={navIconClass} />, onClick: () => navigate(ROUTES.CUSTOMERS_GLOBAL) },
        { key: "customers_restaurant", label: "Client Customers", icon: <UserCircle className={navIconClass} />, onClick: () => navigate(ROUTES.CUSTOMERS_RESTAURANT) },
        { key: "orders", label: "Orders", icon: <ShoppingBag className={navIconClass} />, onClick: () => navigate(ROUTES.ORDERS) },
        { key: "payments", label: "Payments", icon: <CreditCard className={navIconClass} />, onClick: () => navigate(ROUTES.PAYMENTS) },
        { key: "reports", label: "Reports", icon: <TrendingUp className={navIconClass} />, onClick: () => navigate(ROUTES.REPORTS) },
        { key: "drivers", label: "Drivers", icon: <Bike className={navIconClass} />, onClick: () => navigate(ROUTES.DRIVERS) },
        { key: "partners", label: "Partners", icon: <Handshake className={navIconClass} />, onClick: () => navigate(ROUTES.PARTNERS) },
        { key: "app_settings", label: "App Settings", icon: <Smartphone className={navIconClass} />, onClick: () => navigate(ROUTES.APP_SETTINGS) },
        { key: "settings", label: "Settings", icon: <Settings className={navIconClass} />, onClick: () => navigate(ROUTES.SETTINGS) },
      ],
    },
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate(ROUTES.LOGIN);
  };

  const renderSubPage = () => {
    switch (currentKey) {
      case "overview":
        return <OverviewPage />;
      case "clients":
        return <ClientsPage />;
      case "client_detail":
        return <ClientDetailPage id={clientId} />;
      case "partner_detail":
        return <PartnerDetailPage id={partnerId} />;
      case "customers_global":
        return <CustomersGlobalPage />;
      case "customers_restaurant":
        return <CustomersRestaurantPage />;
      case "orders":
        return <OrdersOverviewPage />;
      case "order_detail":
        return <OrderDetailPage id={orderId} />;
      case "payments":
        return <PaymentsPage />;
      case "reports":
        return <ReportsPage />;
      case "drivers":
        return <DriversPage />;
      case "partners":
        return <PartnersPage />;
      case "app_settings":
        return <AppSettingsPage />;
      case "settings":
        return <SettingsPage />;
      default:
        return <OverviewPage />;
    }
  };

  const userDisplayName = user?.email?.split("@")[0] || "Admin";
  const userInitials = userDisplayName.substring(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-[var(--surface-muted)]">
      <Topbar variant="brand" className="px-3 md:px-5 flex items-center gap-2.5 shadow-[0_8px_24px_rgba(17,17,17,0.10)]">
        <button
          className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-white/90 hover:text-white hover:bg-white/15 transition-colors md:hidden"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        <div className="flex items-center min-w-0 py-1">
          {identity.data?.logo_url ? (
            <img
              src={identity.data.logo_url}
              alt={identity.data.brand_name_en}
              draggable={false}
              className="h-[31px] w-auto max-w-[158px] object-contain object-left select-none mix-blend-screen md:h-[36px] md:max-w-[178px]"
            />
          ) : (
            <OeloxLogo variant="header" />
          )}
          <span className="hidden lg:inline-flex ml-2 items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-white/90">
            <Shield className="w-3 h-3" />
            Admin
          </span>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-1 md:gap-1.5">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              className="oelox-topbar-chip cursor-pointer"
            >
              <div className="w-7 h-7 rounded-full bg-white text-[var(--accent)] font-black text-[11px] flex items-center justify-center flex-shrink-0">
                {userInitials}
              </div>
              <div className="text-left hidden sm:block leading-tight">
                <p className="oelox-topbar-chip__label text-white leading-tight">Account</p>
                <p className="oelox-topbar-chip__meta truncate max-w-[160px]">{user?.email}</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-white/70 hidden sm:block transition-transform ${profileDropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {profileDropdownOpen && (
              <div className="absolute right-0 top-[calc(100%+6px)] w-60 z-50 animate-fadeIn">
                <div className="bg-white rounded-2xl border border-[var(--border-subtle)] shadow-[0_12px_40px_rgba(0,0,0,0.1)] overflow-hidden">
                  <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
                    <p className="text-sm font-bold text-[var(--text-primary)] truncate">{userDisplayName}</p>
                    <p className="text-[11px] text-[var(--text-muted)] truncate">{user?.email}</p>
                  </div>
                  <div className="p-1.5">
                    <button
                      onClick={() => { setProfileDropdownOpen(false); navigate(ROUTES.SETTINGS); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--accent)] hover:bg-[var(--accent-light)] rounded-lg transition-colors"
                    >
                      <Settings className="w-4 h-4" /> Settings
                    </button>
                  </div>
                  <div className="p-1.5 border-t border-[var(--border-subtle)]">
                    <button onClick={handleSignOut} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-semibold text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Topbar>

      <Sidebar
        sections={sidebarSections}
        activeKey={activeKey}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onSignOut={handleSignOut}
        signOutLabel="Sign Out"
        workspaceLabel="Platform"
      />

      <div
        style={{ paddingTop: "var(--header-height)" }}
        className={`min-h-0 transition-all duration-300 ease-in-out ${
          sidebarCollapsed ? "md:pl-[var(--sidebar-collapsed)]" : "md:pl-[var(--sidebar-width)]"
        }`}
      >
        <PageSection
          className={`flex-1 animate-slide-up ${
            currentKey === "clients" ||
            currentKey === "client_detail" ||
            currentKey.startsWith("customers") ||
            currentKey === "payments" ||
            currentKey === "drivers"
              ? "page-section--workspace"
              : ""
          }`}
        >
          {renderSubPage()}
        </PageSection>
      </div>

      {mobileMenuOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 md:hidden animate-fadeIn" onClick={() => setMobileMenuOpen(false)} />
          <div className="oelox-sidebar fixed top-0 bottom-0 left-0 w-[var(--sidebar-width)] z-50 md:hidden animate-slide-up overflow-y-auto flex flex-col">
            <div className="flex items-center justify-between h-[var(--header-height)] px-4 shrink-0" style={{ background: "var(--accent)" }}>
              <OeloxLogo variant="header" />
              <button onClick={() => setMobileMenuOpen(false)} className="w-9 h-9 rounded-xl flex items-center justify-center text-white/90 hover:bg-white/15">
                <X className="w-4 h-4" />
              </button>
            </div>
            <nav className="flex-1 py-3 px-2">
              {(sidebarSections[0]?.items ?? []).map((item) => (
                <button
                  key={item.key}
                  onClick={() => { item.onClick(); setMobileMenuOpen(false); }}
                  className={`oelox-sidebar__item oelox-sidebar__item--expanded mb-0.5 ${
                    activeKey === item.key ? "oelox-sidebar__item--active" : ""
                  }`}
                >
                  <span className="oelox-sidebar__item-icon">{item.icon}</span>
                  <span className="oelox-sidebar__item-label">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </>
      )}
    </div>
  );
}
